import { Capacitor, CapacitorHttp } from '@capacitor/core'

/**
 * Shared client transport for the web app and the Capacitor iOS shell.
 *
 * - Web: paths stay relative and requests use `credentials: 'include'`, so the
 *   session cookie set by the server does the auth (upstream behaviour).
 * - Native (Capacitor): requests go through CapacitorHttp to the absolute
 *   `NEXT_PUBLIC_API_BASE_URL`, carry `X-Client-Platform: capacitor`, and send
 *   `Authorization: Bearer <session token>` when one is stored. The token is
 *   returned by /api/auth/login (and register) only when that header is present.
 *
 * Pages should normally go through `@/lib/client-fetch`, which delegates here.
 */

export const API_BASE_URL = (process.env.NEXT_PUBLIC_API_BASE_URL || '').replace(/\/$/, '')

/** localStorage key holding the bearer session token on native. */
export const SESSION_TOKEN_KEY = 'deeliver_session_token'
/** Legacy localStorage key the old mobile build used for the user object. */
export const STORED_USER_KEY = 'user'
/** Window event fired when a native request that carried a token gets a 401. */
export const UNAUTHORIZED_EVENT = 'deeliver:unauthorized'

export function isNativeApp(): boolean {
  try {
    return Capacitor.isNativePlatform()
  } catch {
    return false
  }
}

/**
 * Resolve an API path to the URL the current platform should call.
 * Absolute URLs pass through. On native, relative paths are prefixed with the
 * API base URL; on web they stay relative.
 */
export function apiUrl(path: string): string {
  if (/^https?:\/\//i.test(path)) return path
  const normalized = path.startsWith('/') ? path : `/${path}`
  return isNativeApp() ? `${API_BASE_URL}${normalized}` : normalized
}

/**
 * URL for an EventSource stream. EventSource cannot send an Authorization
 * header, so on native the session token is appended as `?token=` (only
 * /api/location/stream accepts it). On web the URL is the relative path and
 * the session cookie does the auth.
 */
export function streamUrl(path: string): string {
  const url = apiUrl(path)
  if (!isNativeApp()) return url
  const token = getSessionToken()
  if (!token) return url
  return `${url}${url.includes('?') ? '&' : '?'}token=${encodeURIComponent(token)}`
}

/**
 * Origin to use for links that leave the app and come back (e.g. Stripe
 * return_url). On native the WebView origin is capacitor://…, so use the
 * public site; on web use the current origin.
 */
export function appOrigin(): string {
  if (typeof window === 'undefined') return API_BASE_URL
  if (isNativeApp() && API_BASE_URL) return API_BASE_URL
  return window.location.origin
}

export function getSessionToken(): string | null {
  if (typeof window === 'undefined') return null
  try {
    return localStorage.getItem(SESSION_TOKEN_KEY)
  } catch {
    return null
  }
}

export function setSessionToken(token: string): void {
  try {
    localStorage.setItem(SESSION_TOKEN_KEY, token)
  } catch {}
}

/** Remove the bearer token and any stored user object. */
export function clearSession(): void {
  if (typeof window === 'undefined') return
  try {
    localStorage.removeItem(SESSION_TOKEN_KEY)
    localStorage.removeItem(STORED_USER_KEY)
  } catch {}
}

/**
 * Persist `token` from a login/register response body. The server only
 * includes it for native clients, so this is a no-op on web.
 */
export function storeSessionTokenFrom(data: unknown): void {
  if (!isNativeApp()) return
  const token = (data as { token?: unknown } | null)?.token
  if (typeof token === 'string' && token) setSessionToken(token)
}

function abortError(): Error {
  try {
    return new DOMException('The operation was aborted.', 'AbortError')
  } catch {
    const e = new Error('The operation was aborted.')
    e.name = 'AbortError'
    return e
  }
}

const NULL_BODY_STATUSES = new Set([101, 204, 205, 304])

function toResponse(status: number, rawHeaders: Record<string, string> | undefined, data: unknown): Response {
  const body = NULL_BODY_STATUSES.has(status)
    ? null
    : typeof data === 'string'
      ? data
      : JSON.stringify(data ?? {})
  let headers: Headers
  try {
    headers = new Headers(rawHeaders || {})
  } catch {
    headers = new Headers()
  }
  if (body !== null && typeof data !== 'string' && !headers.has('content-type')) {
    headers.set('content-type', 'application/json')
  }
  return new Response(body, { status, headers })
}

async function nativeFetch(url: string, init: RequestInit): Promise<Response> {
  const method = (init.method || 'GET').toUpperCase()

  const headers: Record<string, string> = {}
  new Headers(init.headers || {}).forEach((value, key) => {
    headers[key] = value
  })
  headers['x-client-platform'] = 'capacitor'
  // CapacitorHttp does not send an Origin; some server CSRF checks want one.
  if (!headers['origin'] && API_BASE_URL) headers['origin'] = API_BASE_URL

  const token = getSessionToken()
  if (token && !headers['authorization']) headers['authorization'] = `Bearer ${token}`

  let data: unknown = undefined
  if (init.body != null) {
    if (typeof init.body === 'string') {
      try {
        data = JSON.parse(init.body)
        if (!headers['content-type']) headers['content-type'] = 'application/json'
      } catch {
        data = init.body
      }
    } else {
      data = init.body
    }
  }

  if (init.signal?.aborted) throw abortError()

  let res
  try {
    res = await CapacitorHttp.request({ url, method, headers, data })
  } catch (e: any) {
    console.error('[apiFetch:native] network error', { url, method, message: e?.message || e })
    throw e
  }

  // CapacitorHttp cannot be cancelled; honour the signal for the caller.
  if (init.signal?.aborted) throw abortError()

  if (res.status === 401) {
    clearSession()
    // Only a request that carried a token means "the session is gone".
    // A 401 without one just means the user is signed out.
    if (token && typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent(UNAUTHORIZED_EVENT, { detail: { url } }))
    }
  }

  return toResponse(res.status, res.headers, res.data)
}

/**
 * Fetch an API path on the right transport for the platform. Identity comes
 * from the session (cookie on web, bearer token on native), never from
 * client-supplied headers.
 */
export async function apiFetch(path: string, init: RequestInit = {}): Promise<Response> {
  const url = apiUrl(path)

  if (isNativeApp()) return nativeFetch(url, init)

  try {
    return await fetch(url, { ...init, credentials: init.credentials ?? 'include' })
  } catch (e: any) {
    if (e?.name !== 'AbortError') {
      console.error('[apiFetch] network error', { url, method: init.method || 'GET', message: e?.message || e })
    }
    throw e
  }
}
